import * as THREE from 'three'

export interface LoadSkinOptions {
  /** Hard ceiling for the long edge (clamp to min(GPU max, 2048)). */
  maxSize: number
  /** Anisotropic filtering level. */
  anisotropy: number
  /** COLLADA/Blender UV vertical orientation (per cover type, from manifest). */
  flipY: boolean
  /** Mirror the texture horizontally (U). Some cover meshes are UV-mirrored. */
  flipU?: boolean
}

/**
 * Draw a source image into a 2D canvas at a target size. Used both to downscale
 * and — crucially — as the reliable fallback when createImageBitmap ignores its
 * resize options (which several engines do), so we never upload an oversized image.
 */
function drawToCanvas(src: CanvasImageSource, w: number, h: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')!
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(src, 0, 0, w, h)
  return canvas
}

export interface DecodedImage {
  source: CanvasImageSource
  width: number
  height: number
  /** Free the underlying resource. Call only AFTER drawing the source to a canvas. */
  release(): void
}

/**
 * Decode an image blob into a drawable source. Tries createImageBitmap first
 * (fast, off the main thread), then falls back to an <img> element decode.
 *
 * iOS Safari's createImageBitmap is far pickier than its <img> decoder and throws
 * InvalidStateError ("Cannot decode the data in the argument to createImageBitmap")
 * on many images it renders fine via <img> (large dimensions, progressive/CMYK
 * JPEGs, etc.). The fallback uses a plain <img> load event (NOT img.decode(), which
 * on iOS rejects with EncodingError on large/CMYK images even though the browser can
 * still render and draw them), keeping real covers working on iPhone/iPad — both in
 * the live viewer and the AR/GLB export.
 */
export async function decodeImageBlob(blob: Blob): Promise<DecodedImage> {
  // A non-image content type means the URL didn't return a picture at all — most
  // often a 200 HTML page (404/SPA fallback, login, or error page). Surface that
  // plainly instead of a misleading "can't decode image" further down.
  const type = blob.type
  if (type && !type.startsWith('image/') && type !== 'application/octet-stream') {
    throw new Error(
      `cover URL returned "${type}" (${Math.round(blob.size / 1024)} KB), not an image — check the skin URL`,
    )
  }
  try {
    const bitmap = await createImageBitmap(blob)
    return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() }
  } catch {
    // Fall through to the more lenient <img> load.
  }
  const desc = `${blob.type || 'unknown type'}, ${Math.round(blob.size / 1024)} KB`
  const objectUrl = URL.createObjectURL(blob)
  try {
    const img = new Image()
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error(`<img> failed to load cover (${desc})`))
      img.src = objectUrl
    })
    if (!img.naturalWidth || !img.naturalHeight) {
      throw new Error(`cover decoded to zero size (${desc})`)
    }
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      release: () => URL.revokeObjectURL(objectUrl),
    }
  } catch (e) {
    URL.revokeObjectURL(objectUrl)
    throw e instanceof Error ? e : new Error(String(e))
  }
}

/**
 * Fetch a cover skin and turn it into a GPU-ready THREE.Texture:
 *  - fetched via fetch() (so cross-origin failures surface as errors, not a tainted canvas)
 *  - downscaled to <= maxSize on the long edge, with a canvas fallback when
 *    createImageBitmap resize is unsupported/ignored
 *  - sRGB color space, anisotropy, mipmaps, per-type flipY
 */
export async function loadSkinTexture(url: string, opts: LoadSkinOptions): Promise<THREE.Texture> {
  const res = await fetch(url, { mode: 'cors' })
  if (!res.ok) throw new Error(`Failed to fetch skin (${res.status}) from ${url}`)
  const blob = await res.blob()

  const decoded = await decodeImageBlob(blob)
  const longEdge = Math.max(decoded.width, decoded.height)
  const scale = Math.min(1, opts.maxSize / longEdge)

  let image: TexImageSource
  if (scale < 1) {
    const w = Math.max(1, Math.round(decoded.width * scale))
    const h = Math.max(1, Math.round(decoded.height * scale))
    image = drawToCanvas(decoded.source, w, h)
    decoded.release()
  } else {
    image = decoded.source as unknown as TexImageSource
  }

  const texture = new THREE.Texture(image as unknown as HTMLImageElement)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = Math.max(1, opts.anisotropy)
  texture.flipY = opts.flipY
  texture.generateMipmaps = true
  texture.minFilter = THREE.LinearMipmapLinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.wrapS = THREE.ClampToEdgeWrapping
  texture.wrapT = THREE.ClampToEdgeWrapping
  if (opts.flipU) {
    // Mirror horizontally: reflect U about 0.5.
    texture.wrapS = THREE.RepeatWrapping
    texture.repeat.x = -1
    texture.offset.x = 1
  }
  texture.needsUpdate = true
  return texture
}
